import React, {useState, useEffect, useCallback} from 'react';
import {
  Dialog,
  Box,
  Typography,
} from '@mui/material';
import { ClientData } from '../../types';
import {getPrefilledClientData, profileSettingsItems, profileSettingsOptions} from '../../utils';
import { useSources } from "../../context/SourcesContext";
import {
  combineClientData,
  EMPTY_FIELD_ERROR_MESSAGE,
  findEmptyVisibleFields,
  VISIBLE_PROJECT_SETTINGS_FIELDS,
} from '../settings/clientDataForm';
import ProjectSettingsFields from '../settings/ProjectSettingsFields';
import IconButton from '@mui/material/IconButton';
import { Button, ChevronRight, Cross, OnboardingStepper } from '../../uikit';
import { useAuth } from '../../context/AuthContext';
import { isEmbedded } from '../../lib/embedMode';
import { canDismissOnboarding } from '../../lib/onboardingDismissal';
import SourceDiscoveryDialog from './SourceDiscoveryDialog';
import { useAnswerOnce } from './useAnswerOnce';


interface ClientDataDialogProps {
  open: boolean;
  onClose: () => void;
}

// COM-638/COM-637: Company Type -> Jurisdiction -> Topics of Interest ->
// Document Types, per Confluence 700186650's canonical (1-4) list. No
// "Notification Format" step — it must be hidden in the first
// implementation (VISIBLE_PROJECT_SETTINGS_FIELDS already excludes it).
// This dialog reads step order/count off VISIBLE_PROJECT_SETTINGS_FIELDS
// directly (see TOTAL_STEPS below) so there is no separate step-label array
// to drift out of sync with it — Figma draws Topics second, but Jira's
// order is what ships (human-confirmed 2026-08-15); only the frames' look
// (full-screen card, OnboardingStepper, two-column long lists, button
// styling/order) came from Figma nodes 3339:44965/45153/45411/45565.
const TOTAL_STEPS = VISIBLE_PROJECT_SETTINGS_FIELDS.length;

// Option lists at or above this length render in two columns (Figma:
// Topics of Interest has 14 entries, Document Types has 11); the short
// lists (Company Type: 7, Jurisdiction: 4) stay single-column. Threshold
// sits strictly between the longest short list and the shortest long one.
const TWO_COLUMN_THRESHOLD = 8;

const ClientDataDialog: React.FC<ClientDataDialogProps> = ({
  open,
  onClose,
}) => {
  const { user } = useAuth();
  // See `onboardingDismissal.ts`: standalone app admin only.
  const dismissible = canDismissOnboarding({ embedded: isEmbedded(), isAdmin: Boolean(user?.isAdmin) });

  const [activeStep, setActiveStep] = useState(0);
  const [clientData, setClientData] = useState<ClientData>(getPrefilledClientData([]));
  const [customOptions, setCustomOptions] = useState<ClientData>(getPrefilledClientData([]));
  const [selectedOptionsInputs, setSelectedOptionsInputs] = useState<{ [key: string]: string }>(getPrefilledClientData(''));
  const [error, setError] = useState('');

  // COM-637: the discovery question opens only after this wizard's Dialog
  // has fully closed (see `TransitionProps.onExited` below) - the two
  // dialogs must never be on screen at once. `pendingDiscoveryQuestion` is
  // set by a successful Finish and consumed once the exit transition ends.
  // (There is no other way to close this dialog since item 1 removed the X
  // button/backdrop/Escape paths - Finish is the only exit.)
  const [pendingDiscoveryQuestion, setPendingDiscoveryQuestion] = useState(false);
  const [showDiscoveryQuestion, setShowDiscoveryQuestion] = useState(false);
  // Code-review fix: visual-only, mirrors the useAnswerOnce guard below so
  // the buttons look disabled after the first click. This does NOT close
  // the double-click window by itself - see `useAnswerOnce` for that.
  const [isDiscoveryQuestionAnswered, setIsDiscoveryQuestionAnswered] = useState(false);

  // Code-review fix (COM-637): the kit `Modal` is a plain MUI `Dialog` with
  // the default Fade exit - `setShowDiscoveryQuestion(false)` only *starts*
  // the fade-out, so the Accept/Decline buttons stay mounted and clickable
  // for the whole transition. A double-click/double-tap before the fade
  // finishes would otherwise call `startSourceDiscovery()` twice, doubling
  // the OpenAI spend this dialog exists to gate. `guardDiscoveryAnswer`
  // wraps each handler in a ref check (see useAnswerOnce.ts) so the first
  // response wins and any later one is a no-op, regardless of render
  // timing. Reset only when a fresh question is about to open
  // (`handleWizardExited`), never on re-render.
  const { guard: guardDiscoveryAnswer, reset: resetDiscoveryAnswer } = useAnswerOnce();

  const { setProjectData, selectedProject, canManageProject, startSourceDiscovery } = useSources();

  // Scopes `pendingDiscoveryQuestion` to a single close-then-decide cycle:
  // if the wizard were somehow reopened before its exit transition fired
  // `onExited`, a stale pending flag must not surface the discovery
  // question later as a surprise side effect of this new open. (In
  // practice this can't happen today: the profile-menu entry that used to
  // reopen this dialog is gone, and OnboardingGate only auto-opens it once,
  // on an unconfigured project - see the "Decisions changed during
  // implementation" section of the COM-637 design doc. Kept as a guard in
  // case a future entry point reintroduces reopening.)
  useEffect(() => {
    if (open) {
      setPendingDiscoveryQuestion(false);
    }
  }, [open]);

  useEffect(() => {
    const savedClientData = selectedProject
        ? Object.entries(selectedProject).reduce((res, [key, value]) => {
          if (profileSettingsItems.includes(key as keyof ClientData)) {
            res[key as keyof ClientData] = value;
          }
          return res
        }, {} as ClientData)
        : getPrefilledClientData([]);
    setClientData(savedClientData)
  }, [selectedProject])

  useEffect(() => {
    if (clientData) {
      const selectedCustomOptions = Object.entries(clientData).reduce(
        (res, [type, options]) => {
          const optionList = options.filter(
            (option: string) =>
              !profileSettingsOptions[type as keyof ClientData].includes(option)
          );
          res[type as keyof ClientData] = Array.from(new Set(optionList))
          return res;
        },
        {} as Record<keyof ClientData, string[]>
      );

      setCustomOptions(selectedCustomOptions);
    }
  }, [clientData]);

  const getCurrentStepData = () => VISIBLE_PROJECT_SETTINGS_FIELDS[activeStep] ?? null;

  const currentStepData = getCurrentStepData();

  const currentField = currentStepData?.field ?? null;

  // Clears a shown validation error as soon as the field it complained about
  // stops being empty (predefined selection or custom option added).
  useEffect(() => {
    if (!error || !currentField) return;
    if (clientData[currentField].length > 0 || customOptions[currentField].length > 0) {
      setError('');
    }
  }, [clientData, customOptions, currentField, error]);

  const handleNext = () => {
    if (!currentField) return;
    if (findEmptyVisibleFields(clientData, customOptions).includes(currentField)) {
      setError(EMPTY_FIELD_ERROR_MESSAGE);
      return;
    }
    setError('');
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSave = useCallback(() => {
    // Combine predefined and custom options
    const combinedData = combineClientData(clientData, customOptions);
    setProjectData(combinedData);
    onClose();
    // Finish saves and closes the wizard; the discovery question opens once
    // the close animation finishes (see the Dialog's `onExited` below), not
    // as a side effect of the save call itself (COM-637).
    setPendingDiscoveryQuestion(true);
  }, [clientData, customOptions, onClose, setProjectData]);

  const handleSaveClick = () => {
    if (!currentField) return;
    if (findEmptyVisibleFields(clientData, customOptions).includes(currentField)) {
      setError(EMPTY_FIELD_ERROR_MESSAGE);
      return;
    }
    setError('');
    handleSave();
  };

  const handleCheckboxChange = (field: keyof ClientData, value: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const combinedData = combineClientData(clientData, customOptions);
    setClientData(() => ({
      ...combinedData,
      [field]: checked
        ? [...combinedData[field], value]
        : combinedData[field].filter(item => item !== value),
    }));
  };

  const handleRemoveCustomItem = (field: keyof ClientData, item: string) => {
    setCustomOptions(prev => ({
      ...prev,
      [field]: prev[field].filter(i => i !== item),
    }));
  };

  const handleAddSelectedOption = (field: keyof ClientData) => {
    const customValue = selectedOptionsInputs[field]?.trim();
    if (customValue && !customOptions[field].includes(customValue) && !clientData[field].includes(customValue)) {
      setCustomOptions(prev => ({
        ...prev,
        [field]: [...prev[field], customValue],
      }));
      setSelectedOptionsInputs(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  if (!currentStepData) return null;

  const { field, options, title, description, disabled: isDisabled } = currentStepData;
  const isLastStep = activeStep === TOTAL_STEPS - 1;

  // Consumed once the wizard's own close animation has fully finished -
  // this is what guarantees the two dialogs are never on screen together,
  // rather than opening the question the instant Finish is clicked (which
  // would overlap the wizard's exit fade).
  const handleWizardExited = () => {
    if (pendingDiscoveryQuestion) {
      setPendingDiscoveryQuestion(false);
      // Fresh question, fresh decision window.
      resetDiscoveryAnswer();
      setIsDiscoveryQuestionAnswered(false);
      setShowDiscoveryQuestion(true);
    }
  };

  const handleDeclineDiscovery = guardDiscoveryAnswer(() => {
    setIsDiscoveryQuestionAnswered(true);
    setShowDiscoveryQuestion(false);
  });

  const handleAcceptDiscovery = guardDiscoveryAnswer(() => {
    setIsDiscoveryQuestionAnswered(true);
    startSourceDiscovery();
    setShowDiscoveryQuestion(false);
  });

  return (
    <>
    <Dialog
      open={open}
      // Onboarding is not escapable for the people it was written for (human
      // walkthrough, COM-637 item 1): no close control, `disableEscapeKeyDown`
      // stops Escape, and no `onClose` prop means a backdrop click has nothing
      // to call - finishing is the only way out.
      //
      // The one exception is a standalone app admin (`canDismissOnboarding`):
      // an admin opening a fresh project is inspecting it, not being
      // onboarded, and the full-screen wizard otherwise locks the whole app
      // behind a questionnaire they will not fill in. They get the X and
      // Escape; everyone else keeps the original behaviour. Backdrop dismissal
      // stays off even for them - a full-screen wizard has no visible backdrop
      // to click deliberately, so it would only ever fire by accident.
      disableEscapeKeyDown={!dismissible}
      onClose={(_event, reason) => {
        if (dismissible && reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      fullScreen
      TransitionProps={{ onExited: handleWizardExited }}
      // data-testid, not a role/name query: the Dialog carries no
      // aria-label/aria-labelledby (the step title changes per step, so
      // there is no single fixed accessible name for the dialog itself),
      // and its old "Profile Setup" title text was removed by the
      // full-screen wizard redesign (COM-638) — an e2e locator that keyed
      // off that string (embedded-mode.spec.ts) went stale silently
      // because most of its assertions were `toHaveCount(0)`, which passes
      // vacuously when the locator matches nothing. This testid is the
      // stable hook both that spec and onboarding-wizard.spec.ts use.
      data-testid="onboarding-wizard"
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          position: 'relative',
        }}
      >
        {dismissible && (
          <IconButton
            aria-label="close onboarding"
            onClick={onClose}
            sx={{ position: 'absolute', top: 16, right: 16 }}
          >
            <Cross />
          </IconButton>
        )}
        <Box
          sx={{
            maxWidth: 720,
            width: '100%',
            mx: 'auto',
            px: 4,
            py: 6,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <OnboardingStepper total={TOTAL_STEPS} current={activeStep + 1} />

          <Box sx={{ mt: 4, flex: 1, overflowY: 'auto' }}>
            <ProjectSettingsFields
              field={field}
              title={title}
              description={description}
              options={options}
              clientData={clientData}
              customOptions={customOptions}
              selectedOptionsInputs={selectedOptionsInputs}
              onCheckboxChange={handleCheckboxChange}
              onCustomOptionAdd={handleAddSelectedOption}
              onCustomOptionRemove={handleRemoveCustomItem}
              onOptionInputChange={(changedField, value) =>
                setSelectedOptionsInputs(prev => ({
                  ...prev,
                  [changedField]: value,
                }))
              }
              disabled={isDisabled}
              canManage={canManageProject()}
              twoColumn={options.length >= TWO_COLUMN_THRESHOLD}
            />
            {error && (
              <Typography variant="body2" color="error" sx={{ mt: -1 }}>
                {error}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, pt: 4 }}>
            {isLastStep ? (
              canManageProject() && (
                <Button variant="primary" onClick={handleSaveClick}>
                  Finish Onboarding
                </Button>
              )
            ) : (
              <Button variant="primary" onClick={handleNext} endIcon={<ChevronRight />}>
                Continue
              </Button>
            )}
            {activeStep > 0 && (
              <Button variant="secondary" onClick={handleBack}>
                Back
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
    <SourceDiscoveryDialog
      open={showDiscoveryQuestion}
      onDecline={handleDeclineDiscovery}
      onAccept={handleAcceptDiscovery}
      disabled={isDiscoveryQuestionAnswered}
    />
    </>
  );
};

export default ClientDataDialog;
